import { ref, Ref } from "vue";
import { KoyouKeiyakuService } from "@/service/KoyouKeiyakuService";
import { DialogService } from "@/service/DialogService";
import { SweetAlertResult } from "sweetalert2";
import Swal from 'sweetalert2'
import OptionConst from "@/const/Option";

/*
* 承認処理
*/
export function useApproval() {

  const isApproving = ref(false);

  /* 提出 */
  const submit = async (id: number) => {
    isApproving.value = true;
    const srv = new KoyouKeiyakuService();
    const res = await srv.submit(id)
    isApproving.value = false
    return res
  };

  const approval = async (keiyakuId: number | null, status: number | null) => {
    if (!keiyakuId) {
      return await DialogService.error({
        text: '雇用契約IDが不正です'
      }).then(() => {
        return false
      })
    }

    if (!status) {
      return await DialogService.error({
        text: '承認ステータスを選択してください'
      }).then(() => {
        return false
      })
    }

    isApproving.value = true;
    let dialogOptions = {}

    switch (status) {
      case OptionConst.SYOUNIN_STATUS.APPROVED:
        dialogOptions = {
          customClass: {
            input: 'popup-textarea',
          },
          text: '承認しますか？',
          confirmButtonText: 'はい',
          showCancelButton: true,
          cancelButtonText: 'いいえ',
          input: 'textarea',
          inputPlaceholder: '承認理由を入力(任意)',
          preConfirm: () => {
            const value = Swal.getInput()?.value
            if (value) {
              return value;
            }
            return ''
          }
        }
        break;

      case OptionConst.SYOUNIN_STATUS.DENIED:
        dialogOptions = {
          text: '不採用としますか？',
          confirmButtonText: 'はい',
          showCancelButton: true,
          cancelButtonText: 'いいえ',
          input: 'textarea',
          inputPlaceholder: '不採用理由を入力(必須)',
          preConfirm: () => {
            const value = Swal.getInput()?.value
            if (!value) {
              return Swal.showValidationMessage('不採用理由は必須です')
            }
            return value
          }
        }
        break
      case OptionConst.SYOUNIN_STATUS.REMAND:
        dialogOptions = {
          text: '差し戻しますか？',
          confirmButtonText: 'はい',
          showCancelButton: true,
          cancelButtonText: 'いいえ',
          input: 'textarea',
          inputPlaceholder: '差し戻し理由を入力(必須)',
          preConfirm: () => {
            const value = Swal.getInput()?.value
            if (!value) {
              return Swal.showValidationMessage('差し戻し理由は必須です')
            }
            return value
          }
        }
        break
      case OptionConst.SYOUNIN_STATUS.CANCEL:
        dialogOptions = {
          title: '雇用契約書を取り下げますか？',
          text: '取り下げ後の再提出は新たに作成する必要があります',
          confirmButtonText: 'はい',
          showCancelButton: true,
          cancelButtonText: 'いいえ',
          input: 'textarea',
          inputPlaceholder: '取り下げ理由を入力(必須)',
          preConfirm: () => {
            const value = Swal.getInput()?.value
            if (!value) {
              return Swal.showValidationMessage('取り下げ理由は必須です')
            }
            return value
          }
        }
        break
      default:
        // ここに来る場合、サーバーとクライアントで承認ボタン定数の整合性が取れていないことが考えられる
        return await DialogService.error({
          text: '存在しない承認ステータスが選択されました。処理を中断します'
        }).then(() => {
          return false
        })
    }

    return await DialogService.question(dialogOptions)
      .then(async (result) => {
        if (result.isConfirmed) {
          const input = Swal.getInput();
          let notes: string | null = null
          if (input) {
            notes = input.value ? input.value : null
          }
          const srv = new KoyouKeiyakuService();
          const res = await srv.approval(keiyakuId, status, notes)
          isApproving.value = false
          return res
        } else {
          isApproving.value = false;
          return false
        }
      })
  }

  const check = async (keiyakuId: number | null) => {
    if (!keiyakuId) {
      return await DialogService.error({
        text: '雇用契約IDが不正です'
      }).then(() => {
        return false
      })
    }

    isApproving.value = true;
    const srv = new KoyouKeiyakuService();
    return await DialogService.question({
      customClass: {
        input: 'popup-textarea',
      },
      text: "確認済にしますか？",
      confirmButtonText: 'はい',
      showCancelButton: true,
      cancelButtonText: 'いいえ',
      input: 'textarea',
      inputPlaceholder: 'コメントを入力(任意)',
      preConfirm: () => {
        const value = Swal.getInput()?.value
        if (value) {
          return value;
        }
        return ''
      }
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const input = Swal.getInput();
          let notes: string | null = null
          if (input) {
            notes = input.value ? input.value : null
          }
          isApproving.value = false;
          return await srv.check(keiyakuId, notes)
        } else {
          isApproving.value = false;
          return false
        }
      })
  }

  // 本人承認ステータスを更新する
  const agreement = async (id: number, status: number, notes: string | null = null) => {
    isApproving.value = true;
    const srv = new KoyouKeiyakuService();
    const res = await srv.agreement(id, status, notes)

    if (res) {
      await DialogService.info({
        text: `本人承認ステータスを更新しました。`
      }, true)
      isApproving.value = false;
      return true
    } else {
      await DialogService.error({
        text: '予期せぬ事態が発生し更新に失敗しました'
      }, true);
      isApproving.value = false;
      return false
    }
  }

  // 本人承認ステータス更新の確認ダイアログボックス表示
  const honninSyounin = async (id: number, status: number, label: string, shinseiType: number | null) => {
    isApproving.value = true;

    const title = "本人承認状況を「" + label + "」に更新しますか？";
    // 本人辞退の場合は不採用コメントを必須入力させる
    if (status === OptionConst.SELF_TYPE_DENIED) {
      return await DialogService.question({
        title: title,
        confirmButtonText: '更新します',
        showCancelButton: true,
        cancelButtonText: 'キャンセル',
        input: 'textarea',
        inputPlaceholder: '不採用理由を入力(必須)',
        preConfirm: () => {
          const value = Swal.getInput()?.value
          if (!value) {
            return Swal.showValidationMessage('不採用理由は必須です')
          }
          return value
        }
      }).then(async (result) => {
        if (result.isConfirmed) {
          return await agreement(id, status, Swal.getInput()?.value);
        } else {
          return false
        }
      })
    }
    else {
      let text = ''
      if (
        status === OptionConst.SELF_TYPE_APPROVED &&
        (
          shinseiType === OptionConst.KOYOU_SHINSEI_TYPE.ENTRY ||
          shinseiType === OptionConst.KOYOU_SHINSEI_TYPE.REPLACE_ENTRY
        )
      ) {
        text = '更新が完了すると当該社員の情報が社員マスタへ登録されます。<br><span class="danger-text">以後、承認状況の変更はできなくなりますのでご注意ください。</span>'
      }
      return await DialogService.question({
        title: title,
        html: text,
        showCancelButton: true,
        confirmButtonText: "更新します",
        cancelButtonText: "キャンセル",
      }).then(async (result: SweetAlertResult) => {
        if (result.isConfirmed) {
          return await agreement(id, status);
        } else {
          return false
        }
      });
    }
  };

  // お知らせを既読にするa
  const readDeniedMessage = async (id: number, status: number) => {
    isApproving.value = true;
    const srv = new KoyouKeiyakuService();
    const res = await srv.readDeniedMessage(id)
    if (!res) {
      await DialogService.error({
        text: '否認理由の確認処理に失敗しました'
      })
      isApproving.value = false
      return false
    }
    isApproving.value = false
    return true
  }


  return {
    isApproving,
    submit,
    approval,
    check,
    honninSyounin,
    readDeniedMessage
  }
}

